import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, Outlet, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { formatMoney, PASSWORD_MIN_LENGTH, type RedeemResponse, type UserDTO } from '@va/shared';
import { api, errorMessage } from '../api/client';
import { useDevices, useOrders, useSessions } from '../api/queries';
import { IconGift, IconKey, IconMonitor, IconReceipt, IconUser, IconApple, IconWindows } from '../components/icons';
import { PasswordInput } from '../components/PasswordInput';
import { Empty, ErrorBox, formatDate, Loading, SideNav, Spinner, timeAgo } from '../components/ui';
import { desktop, isDesktop, openExternal } from '../platform';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import { useToast } from '../state/toast';

export function AccountLayout() {
  return (
    <div className="container">
      <div className="page-head">
        <div>
          <span className="eyebrow">Account</span>
          <h1>Your account</h1>
        </div>
      </div>
      <div className="account-layout">
        <SideNav label="Account">
          <NavLink to="/account" end><IconUser size={16} /> Profile</NavLink>
          <NavLink to="/account/security"><IconKey size={16} /> Security</NavLink>
          <NavLink to="/account/orders"><IconReceipt size={16} /> Orders</NavLink>
          <NavLink to="/account/devices"><IconMonitor size={16} /> Devices</NavLink>
          <NavLink to="/redeem"><IconGift size={16} /> Redeem a code</NavLink>
        </SideNav>
        <div className="stack lg">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export function ProfilePage() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const emailChanged = email.trim().toLowerCase() !== user.email;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await api.patch<UserDTO>('/me', { displayName, ...(emailChanged ? { email, currentPassword } : {}) });
      setUser(u);
      setCurrentPassword('');
      toast.success(emailChanged ? 'Saved. Check your inbox to confirm the new address.' : 'Profile saved.');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post('/auth/resend-verification');
      toast.success('Confirmation email sent.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <>
      {!user.emailVerified && (
        <div className="notice warning row between wrap">
          <span>Please confirm your email address ({user.email}). Trials need a confirmed email.</span>
          <button className="btn btn-sm btn-outline" onClick={resend}>Resend email</button>
        </div>
      )}
      <form className="card stack" onSubmit={save}>
        <h3>Profile</h3>
        <div className="split">
          <div className="field">
            <label htmlFor="dn">Name</label>
            <input id="dn" className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="em">Email</label>
            <input id="em" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        {emailChanged && (
          <div className="field">
            <label htmlFor="cp">Current password (required to change email)</label>
            <PasswordInput id="cp" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
        )}
        <div className="row between wrap">
          <span className="small dim">Member since {formatDate(user.createdAt)} · {user.role === 'team' ? 'Team account' : user.role === 'admin' ? 'Admin' : 'Customer'}</span>
          <button className="btn btn-primary" disabled={busy}>{busy ? <Spinner /> : 'Save'}</button>
        </div>
      </form>
      <EmailsCard />
    </>
  );
}

/** Product update emails on or off. Saves on click. */
function EmailsCard() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  // A desktop app offline can show a user saved by an older version, without this setting: on.
  const saved = user?.updateEmails !== false;
  // Shows the click right away, while it saves.
  const [on, setOn] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!busy) setOn(saved);
  }, [saved, busy]);
  if (!user) return null;
  const change = async (next: boolean) => {
    setOn(next);
    setBusy(true);
    try {
      setUser(await api.patch<UserDTO>('/me', { updateEmails: next }));
      toast.success(next ? 'Product update emails are on.' : 'Product update emails are off.');
    } catch (err) {
      setOn(!next);
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card stack">
      <h3 style={{ margin: 0 }}>Emails</h3>
      <label className="checkbox">
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => change(e.target.checked)} />
        <span>
          Product update emails {busy && <Spinner />}
          <span className="small dim" style={{ display: 'block' }}>An email when a product you own or are trying gets a new version.</span>
        </span>
      </label>
    </div>
  );
}

function TwoFactorCard() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const begin = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ secret: string; otpauthUrl: string }>('/me/2fa/setup');
      const qr = await QRCode.toDataURL(r.otpauthUrl, { margin: 1, width: 220, color: { dark: '#111226', light: '#f3f8ff' } });
      setSetup({ ...r, qr });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const enable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      setUser(await api.post<UserDTO>('/me/2fa/enable', { code }));
      setSetup(null);
      setCode('');
      toast.success('Two-factor authentication is on.');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      setUser(await api.post<UserDTO>('/me/2fa/disable', { code, password }));
      setCode('');
      setPassword('');
      toast.success('Two-factor authentication is off.');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card stack">
      <div className="card-title-row">
        <h3 style={{ margin: 0 }}>Two-factor authentication</h3>
        <span className={`pill ${user.totpEnabled ? 'success' : 'muted'}`}>{user.totpEnabled ? 'On' : 'Off'}</span>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Protects your purchases: signing in needs a code from an authenticator app (1Password, Google Authenticator, Authy…) as well as your password.
      </p>
      {!user.totpEnabled && !setup && (
        <div>
          <button className="btn btn-indigo" onClick={begin} disabled={busy}>Set up two-factor</button>
        </div>
      )}
      {setup && (
        <form className="stack" onSubmit={enable}>
          <div className="row wrap" style={{ alignItems: 'flex-start', gap: 20 }}>
            <img src={setup.qr} alt="QR code for your authenticator app" width={180} height={180} style={{ borderRadius: 12 }} />
            <div className="stack" style={{ flex: 1, minWidth: 220 }}>
              <span className="small">1. Scan the QR code with your authenticator app, or enter this key:</span>
              <span className="code-box">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</span>
              <span className="small">2. Enter the 6-digit code it shows:</span>
              <input className="input mono" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-label="Code" />
              <button className="btn btn-primary" disabled={busy || code.length !== 6}>Turn on</button>
            </div>
          </div>
        </form>
      )}
      {user.totpEnabled && (
        <form className="split" onSubmit={disable}>
          <PasswordInput placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Password" />
          <div className="row">
            <input className="input mono" inputMode="numeric" maxLength={6} placeholder="Code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-label="Code" />
            <button className="btn btn-danger" disabled={busy || code.length !== 6 || !password}>Turn off</button>
          </div>
        </form>
      )}
    </div>
  );
}

export function SecurityPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const sessions = useSessions();
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [busy, setBusy] = useState(false);

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/me/password', pw);
      setPw({ currentPassword: '', newPassword: '' });
      toast.success('Password changed. Other sessions were signed out.');
      qc.invalidateQueries({ queryKey: ['sessions'] });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    await api.del(`/me/sessions/${id}`);
    qc.invalidateQueries({ queryKey: ['sessions'] });
  };
  const revokeOthers = async () => {
    await api.post('/me/sessions/revoke-others');
    qc.invalidateQueries({ queryKey: ['sessions'] });
    toast.success('Signed out of all other sessions.');
  };

  return (
    <>
      <form className="card stack" onSubmit={changePassword}>
        <h3>Password</h3>
        <div className="split">
          <PasswordInput autoComplete="current-password" placeholder="Current password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} aria-label="Current password" />
          <PasswordInput autoComplete="new-password" placeholder={`New password (${PASSWORD_MIN_LENGTH}+ characters)`} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} aria-label="New password" />
        </div>
        <div>
          <button className="btn btn-primary" disabled={busy || !pw.currentPassword || pw.newPassword.length < PASSWORD_MIN_LENGTH}>Change password</button>
        </div>
      </form>

      <TwoFactorCard />

      <div className="card stack">
        <div className="card-title-row">
          <h3 style={{ margin: 0 }}>Where you're signed in</h3>
          <button className="btn btn-sm btn-outline" onClick={revokeOthers}>Sign out everywhere else</button>
        </div>
        {sessions.isLoading && <Spinner />}
        {sessions.data?.map((s) => (
          <div key={s.id} className="row between" style={{ padding: '8px 0', borderTop: '1px solid var(--va-line)' }}>
            <div>
              <div style={{ color: 'var(--va-headline)' }}>
                {s.client === 'desktop' ? 'VA Plugin Store app' : 'Web browser'} {s.current && <span className="pill success" style={{ marginLeft: 6 }}>This session</span>}
              </div>
              <div className="small dim">{s.userAgent?.slice(0, 90) ?? 'Unknown device'} · {s.ip ?? ''} · active {timeAgo(s.lastUsedAt)}</div>
            </div>
            {!s.current && <button className="btn btn-sm btn-ghost" onClick={() => revoke(s.id)}>Sign out</button>}
          </div>
        ))}
      </div>
    </>
  );
}

export function OrdersPage() {
  const { data, error, isLoading, refetch } = useOrders();
  const toast = useToast();
  const [opening, setOpening] = useState<string | null>(null);

  /** The PDF receipt: downloaded on the website, opened in the browser from the app. */
  const openReceipt = async (orderId: string) => {
    setOpening(orderId);
    try {
      const { url } = await api.get<{ url: string }>(`/orders/${orderId}/receipt-link`);
      if (desktop) openExternal(url);
      else window.location.assign(`${url}?download=1`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ padding: '18px 22px 6px' }}><h3>Orders</h3></div>
      {error && <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={() => refetch()} /></div>}
      {isLoading && <Loading />}
      {data && data.length === 0 && <div style={{ padding: 22 }}><Empty title="No orders yet">Your receipts will appear here.</Empty></div>}
      {data && data.length > 0 && (
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <table className="table">
            <thead>
              <tr><th>Order</th><th>Date</th><th>Items</th><th>Status</th><th style={{ textAlign: 'right' }}>Total</th><th aria-label="Receipt" /></tr>
            </thead>
            <tbody>
              {data.map((o) => (
                <tr key={o.id}>
                  <td className="mono">{o.number}</td>
                  <td className="nowrap">{formatDate(o.createdAt)}</td>
                  <td>{o.items.map((i) => i.name).join(', ')}</td>
                  <td>
                    <span className={`pill ${o.status === 'paid' ? 'success' : o.status === 'refunded' ? 'muted' : 'warning'}`}>{o.status}</span>
                    {o.testMode && <span className="pill warning" style={{ marginLeft: 6 }}>test</span>}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>{o.totalCents === 0 ? 'Free' : formatMoney(o.totalCents, o.currency)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {o.paidAt && (o.status === 'paid' || o.status === 'refunded') && (
                      <button className="btn btn-sm btn-ghost nowrap" onClick={() => openReceipt(o.id)} disabled={opening === o.id}>
                        {opening === o.id ? <Spinner /> : <IconReceipt size={14} />} Receipt (PDF)
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function DevicesPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [machineId, setMachineId] = useState<string | null>(null);
  useEffect(() => {
    desktop?.license.status().then((s) => setMachineId(s.machineId));
  }, []);
  const { data, error, isLoading, refetch } = useDevices(machineId);

  const deactivate = async (id: string, current?: boolean) => {
    if (!confirm(current ? 'Deactivate this computer? Your plugins here will show the watermark until you sign in again.' : 'Deactivate this computer? Its plugins will show the watermark the next time it refreshes its license.')) return;
    try {
      await api.post(`/devices/${id}/deactivate`);
      toast.success('Computer deactivated.');
      await qc.invalidateQueries({ queryKey: ['devices'] });
      if (desktop) await desktop.license.sync();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="card stack">
      <div>
        <h3>Devices</h3>
        <p className="muted small" style={{ margin: 0 }}>
          Computers where your account unlocks plugins. Deactivate one to free up a slot. Deactivated computers fall back to the watermark when their license next refreshes, or within 30 days if offline.
        </p>
      </div>
      {error && <ErrorBox error={error} onRetry={() => refetch()} />}
      {isLoading && <Spinner />}
      {data && data.length === 0 && <Empty title="No computers yet">Sign in to the VA Plugin Store app on a computer to activate it.</Empty>}
      {data?.map((d) => (
        <div key={d.id} className="row between" style={{ padding: '10px 0', borderTop: '1px solid var(--va-line)' }}>
          <div className="row">
            <span className="icon-btn" style={{ cursor: 'default' }}>{d.platform === 'darwin' ? <IconApple /> : <IconWindows />}</span>
            <div>
              <div style={{ color: 'var(--va-headline)' }}>
                {d.name} {d.current && <span className="pill success" style={{ marginLeft: 6 }}>This computer</span>}
                {d.status === 'revoked' && <span className="pill danger" style={{ marginLeft: 6 }}>Blocked</span>}
              </div>
              <div className="small dim">
                {d.osVersion ?? d.platform} · app {d.appVersion ?? '?'} · last seen {timeAgo(d.lastSeenAt)}
              </div>
            </div>
          </div>
          {d.status === 'active' && <button className="btn btn-sm btn-outline" onClick={() => deactivate(d.id, d.current)}>Deactivate</button>}
        </div>
      ))}
    </div>
  );
}

export function RedeemPage() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RedeemResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const cart = useCart();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post<RedeemResponse>('/codes/redeem', { code });
      setResult(r);
      if (r.kind === 'promo' && r.promo) cart.setCoupon(r.promo.code);
      else {
        if (desktop) await desktop.license.sync();
        await qc.invalidateQueries();
      }
      setCode('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 640 }}>
      <div className="page-head">
        <div>
          <span className="eyebrow">Codes</span>
          <h1>Redeem a code</h1>
        </div>
      </div>
      <form className="card stack" onSubmit={submit}>
        <p className="muted" style={{ margin: 0 }}>Team, gift and promo codes. Products are added to your account and unlock on your activated computers.</p>
        {error && <div className="form-error">{error}</div>}
        {result && (
          <div className="form-ok">
            {result.message}
            {result.granted.length > 0 && (
              <>
                {' '}
                <Link to="/library">Open My Library</Link>.
              </>
            )}
            {result.kind === 'promo' && (
              <>
                {' '}
                <Link to="/cart">Go to your cart</Link>.
              </>
            )}
          </div>
        )}
        <input className="input mono" style={{ fontSize: 18, letterSpacing: '0.12em' }} placeholder="XXXX-XXXX-XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} aria-label="Code" autoFocus />
        <button className="btn btn-primary btn-lg" disabled={busy || code.trim().length < 4}>{busy ? <Spinner /> : 'Redeem'}</button>
        {isDesktop && <p className="small dim" style={{ margin: 0 }}>Licenses update on this computer straight away.</p>}
      </form>
    </div>
  );
}
